import * as React from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import { CheckIcon, LoaderCircleIcon, SparklesIcon, SquareIcon, XIcon } from 'lucide-react';

import { ApiError, useProofManagedDocument } from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AI_PROOFING_MAX_PARAGRAPHS,
  acceptAiProofingSuggestion,
  aiProofingSuggestionAt,
  aiProofingSuggestions,
  clearAiProofing,
  collectAiProofingParagraphs,
  dismissAiProofingSuggestion,
  showAiProofingResults,
  trackAiProofingParagraphs,
  untrackAiProofingParagraphs,
  type AiProofingSuggestion,
} from './document-ai-proofing';

/**
 * AI proofing on request. The ribbon asks the controller (always mounted with the editor) to check the
 * selection or the text from the cursor down; suggestions are underlined and each one is accepted or
 * dismissed by the person. Nothing in the document changes without an Accept.
 */
export type AiProofingStatus =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'nothing' }
  | { kind: 'done'; checked: number; more: boolean; skippedLong: number }
  | { kind: 'changed' }
  | { kind: 'error'; message: string };

interface AiProofingRunner { check: () => void; stop: () => void }

/** Connects the ribbon (mounted only while Review is open) to the controller that owns requests. */
export class AiProofingStore {
  private snapshot: { status: AiProofingStatus; ready: boolean } = { status: { kind: 'idle' }, ready: false };
  private runner: AiProofingRunner | null = null;
  private readonly listeners = new Set<() => void>();

  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getSnapshot = () => this.snapshot;
  setStatus(status: AiProofingStatus): void { this.publish({ ...this.snapshot, status }); }
  setRunner(runner: AiProofingRunner | null): void { this.runner = runner; this.publish({ status: { kind: 'idle' }, ready: Boolean(runner) }); }
  check(): void { this.runner?.check(); }
  stop(): void { this.runner?.stop(); }
  private publish(next: { status: AiProofingStatus; ready: boolean }): void {
    this.snapshot = next;
    for (const listener of this.listeners) listener();
  }
}

/** Plain-language message for a failed check. */
export function aiProofingErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'AI_HUMAN_ONLY') return 'AI proofing is turned off by your administrator. Spell check still works.';
    if (error.code === 'DOCUMENT_ARCHIVED') return 'Archived documents cannot be checked with AI.';
    if (error.status === 403) return 'You do not have permission to use AI on this document.';
    if (error.status === 404) return 'This document is no longer available.';
    if (error.status === 429) return error.message || 'AI proofing is paused briefly. Try again shortly.';
    if (error.status === 503 || error.status === 502 || error.status === 504 || error.code === 'FEATURE_DISABLED' || error.code?.startsWith('AI_')) {
      return 'AI proofing is unavailable right now. Spell check still works.';
    }
  }
  return 'AI proofing could not complete. Try again.';
}

function statusText(status: AiProofingStatus, count: number): string {
  switch (status.kind) {
    case 'checking': return 'Checking with AI…';
    case 'nothing': return 'Nothing to check here. Place the cursor or select some text.';
    case 'changed': return 'That text changed, so the suggestion was removed.';
    case 'error': return status.message;
    case 'done': {
      const found = count === 0 ? 'No AI suggestions.' : `${count} AI suggestion${count === 1 ? '' : 's'} to review.`;
      const more = status.more ? ' More text remains below; move the cursor there and check again.' : '';
      const long = status.skippedLong ? ` ${status.skippedLong} long paragraph${status.skippedLong === 1 ? ' was' : 's were'} skipped.` : '';
      return found + more + long;
    }
    default: return '';
  }
}

function useSuggestions(editor: Editor): AiProofingSuggestion[] {
  return useEditorState({ editor, selector: ({ editor: current }) => (current ? aiProofingSuggestions(current.state) : []) }) ?? [];
}

function suggestionLabel(suggestion: Pick<AiProofingSuggestion, 'original' | 'replacement'>): string {
  return suggestion.replacement ? `Change “${suggestion.original}” to “${suggestion.replacement}”` : `Remove “${suggestion.original}”`;
}

function accept(editor: Editor, store: AiProofingStore, suggestion: AiProofingSuggestion): void {
  if (acceptAiProofingSuggestion(editor, suggestion.id)) return;
  dismissAiProofingSuggestion(editor, suggestion.id);
  store.setStatus({ kind: 'changed' });
}

/** Review → Proofing controls: check, stop, and the list of suggestions to accept or dismiss. */
export function AiProofingRibbon({ editor, store, readOnly }: { editor: Editor; store: AiProofingStore; readOnly: boolean }) {
  const { status, ready } = React.useSyncExternalStore(store.subscribe, store.getSnapshot);
  const suggestions = useSuggestions(editor);
  if (!ready) return null;
  const checking = status.kind === 'checking';
  const message = statusText(status, suggestions.length);
  return <>
    {checking
      ? <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => store.stop()}>
        <SquareIcon aria-hidden /> Stop checking
      </Button>
      : <Button type="button" variant="ghost" size="sm" className="h-9" disabled={readOnly} onClick={() => store.check()}
        title="Check the selection, or the text from the cursor down, for spelling and grammar with AI">
        <SparklesIcon aria-hidden /> Check with AI
      </Button>}
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-9" disabled={!suggestions.length}>
          AI suggestions{suggestions.length ? ` (${suggestions.length})` : ''}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-96 w-80 overflow-y-auto" aria-label="AI suggestions">
        {suggestions.map(suggestion => <DropdownMenuGroup key={suggestion.id} aria-label={suggestionLabel(suggestion)}>
          <DropdownMenuLabel className="space-y-0.5 font-normal">
            <span className="block text-sm font-medium">{suggestionLabel(suggestion)}</span>
            <span className="block text-xs text-muted-foreground">{suggestion.kind === 'grammar' ? 'Grammar' : 'Spelling'} · {suggestion.reason}</span>
          </DropdownMenuLabel>
          <DropdownMenuItem disabled={readOnly} onSelect={() => accept(editor, store, suggestion)}><CheckIcon aria-hidden /> Accept</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => dismissAiProofingSuggestion(editor, suggestion.id)}><XIcon aria-hidden /> Dismiss</DropdownMenuItem>
          <DropdownMenuSeparator />
        </DropdownMenuGroup>)}
        <DropdownMenuItem onSelect={() => clearAiProofing(editor)}>Dismiss all</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    {message && <span role="status" aria-live="polite" className="max-w-72 self-center text-xs text-muted-foreground">
      {checking && <LoaderCircleIcon className="mr-1 inline size-3 animate-spin" aria-hidden />}{message}
    </span>}
  </>;
}

let runCounter = 0;

/**
 * Owns AI proofing requests for one editor: batches of at most eight paragraphs, sent one after another,
 * aborted on Stop, on a new check, when the document changes or when the editor closes.
 */
export function AiProofingController({ editor, store, documentId, readOnly }: {
  editor: Editor;
  store: AiProofingStore;
  documentId: string;
  readOnly: boolean;
}) {
  const abort = React.useRef<AbortController | null>(null);
  const proof = useProofManagedDocument({ mutation: { retry: false }, request: { get signal() { return abort.current?.signal; } } });
  const mutate = React.useRef(proof.mutateAsync);
  mutate.current = proof.mutateAsync;

  React.useEffect(() => {
    let disposed = false;
    const stop = () => {
      abort.current?.abort();
      abort.current = null;
      if (!disposed) store.setStatus({ kind: 'idle' });
    };
    const check = async () => {
      if (editor.isDestroyed || !editor.isEditable) return;
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      runCounter += 1;
      const selection = collectAiProofingParagraphs(editor.state, `r${runCounter}`);
      if (!selection.paragraphs.length) { store.setStatus({ kind: 'nothing' }); return; }
      store.setStatus({ kind: 'checking' });
      const ids = selection.paragraphs.map(paragraph => paragraph.id);
      trackAiProofingParagraphs(editor, selection.paragraphs);
      try {
        for (let index = 0; index < selection.paragraphs.length; index += AI_PROOFING_MAX_PARAGRAPHS) {
          const batch = selection.paragraphs.slice(index, index + AI_PROOFING_MAX_PARAGRAPHS);
          const result = await mutate.current({ id: documentId, data: { paragraphs: batch.map(({ id, text }) => ({ id, text })) } });
          if (controller.signal.aborted || disposed || editor.isDestroyed) return;
          showAiProofingResults(editor, batch.map(paragraph => paragraph.id), result.issues);
        }
        abort.current = null;
        store.setStatus({ kind: 'done', checked: selection.paragraphs.length, more: selection.more, skippedLong: selection.skippedLong });
      } catch (error) {
        if (controller.signal.aborted || disposed || editor.isDestroyed) return;
        abort.current = null;
        untrackAiProofingParagraphs(editor, ids);
        store.setStatus({ kind: 'error', message: aiProofingErrorMessage(error) });
      }
    };
    store.setRunner({ check: () => { void check(); }, stop });
    return () => {
      disposed = true;
      abort.current?.abort();
      abort.current = null;
      store.setRunner(null);
    };
  }, [documentId, editor, store]);

  React.useEffect(() => {
    if (!readOnly) return;
    abort.current?.abort();
    abort.current = null;
    store.setStatus({ kind: 'idle' });
  }, [readOnly, store]);

  return readOnly ? null : <AiProofingContextMenu editor={editor} store={store} />;
}

type MenuState = { open: false } | { open: true; suggestion: AiProofingSuggestion; x: number; y: number };

/** Right-click (or the context-menu key) on an AI suggestion: accept it, see why, or dismiss it. */
export function AiProofingContextMenu({ editor, store }: { editor: Editor; store: AiProofingStore }) {
  const [menu, setMenu] = React.useState<MenuState>({ open: false });

  React.useEffect(() => {
    if (editor.isDestroyed) return;
    const dom = editor.view.dom;
    const onContextMenu = (event: MouseEvent) => {
      let x = event.clientX;
      let y = event.clientY;
      let suggestion: AiProofingSuggestion | null = null;
      const element = event.target instanceof Element ? event.target.closest('.ai-proofing-issue') : null;
      if (element && dom.contains(element)) suggestion = aiProofingSuggestionAt(editor.state, editor.view.posAtDOM(element, 0));
      if (!suggestion && event.button !== 2 && x === 0 && y === 0) {
        suggestion = aiProofingSuggestionAt(editor.state, editor.state.selection.from);
        if (suggestion) {
          const coords = editor.view.coordsAtPos(suggestion.to);
          x = coords.left;
          y = coords.bottom;
        }
      }
      if (!suggestion) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setMenu({ open: true, suggestion, x, y });
    };
    dom.addEventListener('contextmenu', onContextMenu, true);
    return () => dom.removeEventListener('contextmenu', onContextMenu, true);
  }, [editor]);

  if (!menu.open) return null;
  const { suggestion } = menu;
  const close = () => setMenu({ open: false });
  const current = () => aiProofingSuggestions(editor.state).find(item => item.id === suggestion.id) ?? suggestion;
  return <DropdownMenu open onOpenChange={open => { if (!open) close(); }} modal={false}>
    <DropdownMenuTrigger asChild>
      <span aria-hidden="true" className="pointer-events-none fixed size-0" style={{ left: menu.x, top: menu.y }} />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" side="bottom" sideOffset={2} className="w-72" aria-label="AI suggestion">
      <DropdownMenuItem className="font-medium" onSelect={() => { accept(editor, store, current()); close(); }}>
        <CheckIcon aria-hidden /> {suggestion.replacement ? suggestion.replacement : `Remove “${suggestion.original}”`}
      </DropdownMenuItem>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        {suggestion.kind === 'grammar' ? 'Grammar' : 'Spelling'} · {suggestion.reason}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => { dismissAiProofingSuggestion(editor, suggestion.id); editor.commands.focus(); close(); }}>
        <XIcon aria-hidden /> Dismiss
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>;
}
